import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  UserPlusIcon,
  UsersIcon,
  QrCodeIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/useAuthStore';
import { useCompanyStore } from '../../stores/useCompanyStore';
import { useProductosStore } from '../../stores/useProductosStore';
import { useSubscriptionsStore, MemberSubscription } from '../../stores/useSubscriptionsStore';
import { useClientesStore } from '../../stores/useClientesStore';
import { Producto } from '../../types';
import { ModalAltaMembresia } from '../../components/suscripciones/ModalAltaMembresia';
import { RecordPaymentModal } from '../../components/suscripciones/RecordPaymentModal';
import { PaymentStatusBadge } from '../../components/suscripciones/PaymentStatusBadge';

function getProductName(sub: MemberSubscription): string {
  const p = sub.producto;
  if (!p) return '—';
  const prod = Array.isArray(p) ? p[0] : p;
  return (prod as { nombre?: string })?.nombre ?? '—';
}

function getClienteName(sub: MemberSubscription): string {
  // clientes table has nombre + apellido (no nombre_completo); support both for compatibility
  const c = sub.cliente;
  if (!c) return '—';
  const cli = Array.isArray(c) ? c[0] : c;
  const o = cli as { nombre?: string; apellido?: string; nombre_completo?: string };
  return (o?.nombre_completo ?? `${o?.nombre || ''} ${o?.apellido || ''}`.trim()) || '—';
}

function getCheckInMethodLabel(method: string): string {
  switch (method) {
    case 'qr_self':
      return 'QR / Autoservicio';
    case 'manual':
      return 'Manual';
    case 'app':
      return 'App';
    case 'kiosk':
      return 'Kiosco';
    default:
      return method || '—';
  }
}

type GestionSuscripcionesProps = {
  /** Cuando true (p. ej. pestaña dentro de Productos), se omite el título principal y el padding ancho de página. */
  embed?: boolean;
  /** En embed, pasar el id desde el padre evita depender solo del store y reduce re-suscripciones al estado global. */
  empresaId?: string;
};

export function GestionSuscripciones({ embed = false, empresaId: empresaIdProp }: GestionSuscripcionesProps) {
  const empresaIdFromStore = useCompanyStore((s) => s.currentEmpresa?.id);
  const hasEmpresaInStore = useCompanyStore((s) => !!s.currentEmpresa);
  const empresaId =
    embed && empresaIdProp ? empresaIdProp : empresaIdFromStore ? empresaIdFromStore : undefined;

  const usuario = useAuthStore((s) => s.usuario);
  const productos = useProductosStore((s) => s.productos);
  const subscriptions = useSubscriptionsStore((s) => s.subscriptions);
  const lastAttendanceBySubscriptionId = useSubscriptionsStore((s) => s.lastAttendanceBySubscriptionId);
  const isLoading = useSubscriptionsStore((s) => s.isLoading);
  const fetchSubscriptions = useSubscriptionsStore((s) => s.fetchSubscriptions);
  const getOverdueSubscriptions = useSubscriptionsStore((s) => s.getOverdueSubscriptions);
  const getDueSoonSubscriptions = useSubscriptionsStore((s) => s.getDueSoonSubscriptions);
  const clientes = useClientesStore((s) => s.clientes);

  const [showAltaModal, setShowAltaModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState<MemberSubscription | null>(null);
  const [filterProductId, setFilterProductId] = useState<string | null>(null);
  const [searchMember, setSearchMember] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [paymentsByMonth, setPaymentsByMonth] = useState<unknown[]>([]);

  useEffect(() => {
    if (!usuario?.id || hasEmpresaInStore) return;
    void useCompanyStore.getState().fetchUserEmpresas(usuario.id);
  }, [usuario?.id, hasEmpresaInStore]);

  useEffect(() => {
    if (!empresaId) return;
    let cancelled = false;
    const { fetchProductos } = useProductosStore.getState();
    const { fetchLastAttendancePerSubscription, updatePaymentStatuses } = useSubscriptionsStore.getState();
    const { fetchClientes } = useClientesStore.getState();

    void (async () => {
      // En embed, Productos ya dispara fetchProductos por empresa; evita duplicar el GET.
      const parallel: Promise<unknown>[] = [
        fetchClientes(empresaId),
        fetchLastAttendancePerSubscription(empresaId),
      ];
      if (!embed) {
        parallel.push(fetchProductos(empresaId));
      }
      await Promise.all(parallel);
      if (cancelled) return;
      // Un solo fetch de membresías: RPC (si aplica) y luego select (updatePaymentStatuses ya no duplica con fs previo).
      await updatePaymentStatuses(empresaId);
    })();

    return () => {
      cancelled = true;
    };
  }, [empresaId, embed]);

  useEffect(() => {
    if (!empresaId) return;
    let ignore = false;
    useSubscriptionsStore
      .getState()
      .getPaymentsByMonth(empresaId, selectedYear, selectedMonth)
      .then((rows) => {
        if (!ignore) setPaymentsByMonth(rows);
      });
    return () => {
      ignore = true;
    };
  }, [empresaId, selectedYear, selectedMonth]);

  const subscriptionProducts: Producto[] = (productos || []).filter(
    (p) => p.esSuscripcion === true
  );

  const filteredMembers = subscriptions.filter((sub) => {
    if (filterProductId && sub.producto_id !== filterProductId) return false;
    if (searchMember.trim()) {
      const name = getClienteName(sub).toLowerCase();
      const productName = getProductName(sub).toLowerCase();
      const term = searchMember.trim().toLowerCase();
      return name.includes(term) || productName.includes(term);
    }
    return true;
  });

  const handleAltaSuccess = () => {
    setShowAltaModal(false);
    if (empresaId) void fetchSubscriptions(empresaId);
  };

  const overdueSubs = empresaId ? getOverdueSubscriptions(empresaId) : [];
  const dueSoonSubs = empresaId ? getDueSoonSubscriptions(empresaId) : [];
  const activeSubs = subscriptions.filter((s) => s.status === 'active');
  const totalMRR = activeSubs.reduce((sum, sub) => {
    const p = sub.producto;
    const prod = !p ? null : Array.isArray(p) ? p[0] : p;
    return sum + (Number((prod as { precio?: number })?.precio) || 0);
  }, 0);

  if (!empresaId) {
    return (
      <div className="p-6 text-gray-500">
        Selecciona una empresa para gestionar suscripciones.
      </div>
    );
  }

  return (
    <div className={embed ? 'max-w-none' : 'max-w-6xl mx-auto p-6'}>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {!embed && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Suscripciones y membresías</h1>
            <p className="text-gray-600 mt-1">
              Planes vinculados al catálogo. Inscribe clientes y revisa quiénes pueden usar el check-in por QR.
            </p>
          </div>
        )}
        <div
          className={`flex flex-wrap items-center gap-2 ${embed ? 'w-full sm:justify-end' : ''}`}
        >
          <Link
            to="/configuracion/qr-code"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
          >
            <QrCodeIcon className="h-5 w-5 mr-2" />
            Código QR
          </Link>
          <button
            type="button"
            onClick={() => setShowAltaModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <UserPlusIcon className="h-5 w-5 mr-2" />
            Dar de alta membresía
          </button>
        </div>
      </div>

      {/* Overdue / Due soon alerts */}
      {overdueSubs.length > 0 && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex flex-wrap items-center gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-red-900">
              {overdueSubs.length} membresía{overdueSubs.length > 1 ? 's' : ''} con pago vencido
            </p>
            <p className="text-sm text-red-700">
              Clientes con pagos atrasados. Revisa la lista y registra el pago.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setFilterProductId(null)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
          >
            Ver en lista
          </button>
        </div>
      )}

      {dueSoonSubs.length > 0 && overdueSubs.length === 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <ClockIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-amber-900">
              {dueSoonSubs.length} pago{dueSoonSubs.length > 1 ? 's' : ''} próximo{dueSoonSubs.length > 1 ? 's' : ''} a vencer
            </p>
            <p className="text-sm text-amber-700">Vencen en los próximos 3 días</p>
          </div>
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm text-gray-600">Membresías activas</p>
            <CheckCircleIcon className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{activeSubs.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm text-gray-600">Ingresos mensuales</p>
            <CurrencyDollarIcon className="h-5 w-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {totalMRR.toLocaleString('es-CO', { minimumFractionDigits: 0 })}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm text-gray-600">Pagos vencidos</p>
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
          </div>
          <p className="text-2xl font-bold text-red-600">{overdueSubs.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm text-gray-600">Próximos a vencer</p>
            <ClockIcon className="h-5 w-5 text-amber-600" />
          </div>
          <p className="text-2xl font-bold text-amber-700">{dueSoonSubs.length}</p>
        </div>
      </div>

      {/* Pagos por mes */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <BanknotesIcon className="h-5 w-5 text-green-600" />
          Pagos por mes
        </h2>
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Mes</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                <option key={m} value={m}>
                  {new Date(2000, m - 1, 1).toLocaleDateString('es-CO', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Año</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              {[0, 1, 2].map((i) => {
                const y = new Date().getFullYear() - i;
                return (
                  <option key={y} value={y}>
                    {y}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        {Array.isArray(paymentsByMonth) && paymentsByMonth.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">No hay pagos registrados en este mes.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Método</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(paymentsByMonth as Array<{ id?: string; payment_date?: string; amount?: number; payment_method?: string; subscription?: { cliente?: { nombre?: string; apellido?: string } | { nombre?: string; apellido?: string }[]; producto?: unknown } }>).map((pay, idx) => {
                  const sub = pay.subscription;
                  const cliente = Array.isArray(sub?.cliente) ? sub?.cliente?.[0] : sub?.cliente;
                  const name = cliente ? [cliente.nombre, cliente.apellido].filter(Boolean).join(' ').trim() || '—' : '—';
                  return (
                    <tr key={(pay as { id?: string }).id || idx}>
                      <td className="px-3 py-2 text-gray-900">
                        {pay.payment_date ? new Date(pay.payment_date).toLocaleDateString('es-CO') : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-900">{name}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {Number(pay.amount ?? 0).toLocaleString('es-CO')}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{pay.payment_method || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* All members list */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <UsersIcon className="h-5 w-5 text-blue-600" />
          Miembros inscritos
        </h2>
        <p className="text-gray-600 text-sm mb-4">
          Quienes tengan una membresía activa aparecerán al escanear el QR de check-in (buscar por nombre o teléfono).
          La columna <strong>Última entrada</strong> muestra cuándo y cómo ingresó por última vez cada miembro (Manual o QR).
        </p>

        <div className="mb-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre o plan..."
              value={searchMember}
              onChange={(e) => setSearchMember(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={filterProductId ?? ''}
            onChange={(e) => setFilterProductId(e.target.value || null)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos los planes</option>
            {subscriptionProducts.map((p) => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            ))}
          </select>
        </div>

        {isLoading ? (
          <div className="py-12 text-center text-gray-500">Cargando miembros...</div>
        ) : filteredMembers.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-600">
            {subscriptions.length === 0
              ? 'Aún no hay miembros. Usa "Dar de alta membresía" para inscribir a un cliente en un plan.'
              : 'No hay miembros que coincidan con el filtro.'}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pago</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vence</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clases rest.</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" title="Cuándo y cómo ingresó por última vez (Manual o QR)">Última entrada</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredMembers.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{getClienteName(sub)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{getProductName(sub)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          sub.status === 'active'
                            ? 'px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800'
                            : sub.status === 'suspended'
                            ? 'px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800'
                            : 'px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700'
                        }
                      >
                        {sub.status === 'active' ? 'Activo' : sub.status === 'suspended' ? 'Suspendido' : sub.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <PaymentStatusBadge paymentStatus={sub.payment_status} daysOverdue={sub.days_overdue ?? 0} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {sub.current_period_end
                        ? new Date(sub.current_period_end).toLocaleDateString('es-CO')
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {sub.classes_remaining != null ? sub.classes_remaining : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {(() => {
                        const last = lastAttendanceBySubscriptionId[sub.id];
                        if (!last) return <span className="text-gray-400" title="Sin registros de check-in">Nunca</span>;
                        const d = new Date(last.check_in_time);
                        return (
                          <span title={`${d.toLocaleString('es-CO')} · ${getCheckInMethodLabel(last.check_in_method)}`}>
                            <span className="font-medium text-gray-900">
                              {d.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                            <span className="block text-xs text-gray-500 mt-0.5">
                              {getCheckInMethodLabel(last.check_in_method)}
                            </span>
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      {sub.status === 'active' && (
                        <button
                          type="button"
                          onClick={() => setShowPaymentModal(sub)}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Registrar pago
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showAltaModal && (
        <ModalAltaMembresia
          empresaId={empresaId}
          subscriptionProducts={subscriptionProducts}
          clientes={clientes}
          onClose={() => setShowAltaModal(false)}
          onSuccess={handleAltaSuccess}
        />
      )}

      {showPaymentModal && (
        <RecordPaymentModal
          subscription={showPaymentModal}
          onClose={() => setShowPaymentModal(null)}
          onSuccess={() => {
            setShowPaymentModal(null);
            if (!empresaId) return;
            const sub = useSubscriptionsStore.getState();
            void sub.updatePaymentStatuses(empresaId);
            void sub.getPaymentsByMonth(empresaId, selectedYear, selectedMonth).then(setPaymentsByMonth);
          }}
        />
      )}
    </div>
  );
}
